import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Card, Table, Input, Typography, Space, Tooltip } from 'antd';
import { GiftFilled } from '@ant-design/icons';
import { formatCurrency2SigFig } from '../../../core/utils/campaigns';
import './styles.css';

const { Title, Text } = Typography;

const CampaignRequestorReviewBudget = ({ formValues, onBudgetChange }) => {
  const { campaigns = [], budget: totalBudget = 0 } = formValues || {};
  const [editableCampaigns, setEditableCampaigns] = useState([]);

  // Initialize editable campaigns data
  useEffect(() => {
    const initialData = campaigns.map((campaign, index) => {
      const budgetTotal = campaign.budgetTotal || 0;
      // Calculate initial percentage if budget exists
      const budgetPercentage = totalBudget > 0 && budgetTotal > 0
        ? (budgetTotal / totalBudget * 100).toFixed(1)
        : campaign.budgetPercentage || 0;

      return {
        key: index,
        index: index + 1,
        name: campaign.name || `Line Item ${index + 1}`,
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        impressions: campaign.impressions || 0,
        budgetTotal,
        budgetPercentage,
        ...campaign
      };
    });
    setEditableCampaigns(initialData);
  }, [campaigns, totalBudget]);

  // Handle budget change
  const handleBudgetChange = (index, field, value) => {
    const updatedCampaigns = [...editableCampaigns];
    updatedCampaigns[index] = {
      ...updatedCampaigns[index],
      [field]: value
    };

    // If budget total changes, recalculate percentage
    if (field === 'budgetTotal' && totalBudget > 0) {
      const percentage = (parseFloat(value) / totalBudget * 100).toFixed(1);
      updatedCampaigns[index].budgetPercentage = percentage;
    }

    // If percentage changes, recalculate budget total
    if (field === 'budgetPercentage' && totalBudget > 0) {
      const budgetAmount = (totalBudget * parseFloat(value) / 100).toFixed(2);
      updatedCampaigns[index].budgetTotal = budgetAmount;
    }

    setEditableCampaigns(updatedCampaigns);

    // Notify parent component of changes
    if (onBudgetChange) {
      onBudgetChange(updatedCampaigns);
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: '8%',
    },
    {
      title: 'Line Item Name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
    },
    {
      title: 'Duration',
      key: 'duration',
      width: '20%',
      render: (_, record) => (
        <Text style={{ fontSize: '14px' }}>
          {record.startDate && record.endDate
            ? `${moment(record.startDate).format('MM/DD/YYYY')} - ${moment(record.endDate).format('MM/DD/YYYY')}`
            : 'Not specified'
          }
        </Text>
      ),
    },
    {
      title: 'Imps.',
      dataIndex: 'impressions',
      key: 'impressions',
      width: '12%',
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>{(text || 0).toLocaleString()}</Text>
      ),
    },
    {
      title: 'Budget',
      key: 'budget',
      width: '20%',
      render: (_, record, index) => (
        <Space size={4}>
          <Input
            value={record.budgetTotal}
            onChange={(e) => handleBudgetChange(index, 'budgetTotal', e.target.value)}
            prefix="$"
            style={{ width: '80px' }}
            size="small"
          />
          <Input
            value={record.budgetPercentage}
            onChange={(e) => handleBudgetChange(index, 'budgetPercentage', e.target.value)}
            suffix="%"
            style={{ width: '60px' }}
            size="small"
          />
        </Space>
      ),
    },
    {
      title: 'Value Added',
      key: 'valueAdded',
      width: '15%',
      align: 'center',
      render: (_, record) => {
        return record.valueAdd ? (
          <Tooltip title="Yes">
            <GiftFilled
              style={{
                fontSize: 20,
                color: '#722ed1',
              }}
            />
          </Tooltip>
        ) : null;
      },
    },
  ];

  // Calculate totals
  const totalImpressions = editableCampaigns.reduce((sum, campaign) => sum + (campaign.impressions || 0), 0);
  const totalBudgetUsed = editableCampaigns.reduce((sum, campaign) => sum + (parseFloat(campaign.budgetTotal) || 0), 0);

  return (
    <Card>
      <div className="requester-table-wrapper">
        <div className="requester-table-header">
          <Title level={5}>BUDGET ALLOCATION</Title>
        </div>

        <Table
          className="requester-table"
          columns={columns}
          dataSource={editableCampaigns}
          pagination={false}
          size="small"
          footer={() => (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
              <Text strong>
                {editableCampaigns.length} Line Item{editableCampaigns.length !== 1 ? 's' : ''}
              </Text>
              <Space size="large">
                <Text>
                  <Text type="secondary">Total Impressions: </Text>
                  <Text strong>{totalImpressions.toLocaleString()}</Text>
                </Text>
                <Text>
                  <Text type="secondary">Budget Used: </Text>
                  <Text strong>{formatCurrency2SigFig(totalBudgetUsed)} / {formatCurrency2SigFig(totalBudget)}</Text>
                </Text>
              </Space>
            </div>
          )}
        />
      </div>
    </Card>
  );
};

export default CampaignRequestorReviewBudget;